import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { guacHttpClient } from "@/lib/http";
import { JobsResponse, UpdateJobManagerRequest } from "@/types";

// Adds/removes a job manager. Optimistic so the multiselect reacts instantly;
// the dashboard refetches since managed jobs surface there.
export function useUpdateSetJobManager() {
  const currentCompanyId = useCurrentCompanyId();
  const queryClient = useQueryClient();
  const jobsQueryKey = ["jobs", currentCompanyId];

  return useMutation({
    mutationFn: async ({
      jobId,
      userId,
      isManager,
    }: UpdateJobManagerRequest) => {
      await guacHttpClient.patch("job-users/managers", {
        jobId,
        userId,
        isManager,
      });
    },
    onMutate: ({ jobId, userId, isManager }) => {
      const previous = queryClient.getQueryData<JobsResponse[]>(jobsQueryKey);

      queryClient.setQueryData(
        jobsQueryKey,
        (cachedData: JobsResponse[] | undefined) => {
          if (!cachedData) return cachedData;
          return cachedData.map((jobResponse) => {
            if (jobResponse.job.id !== jobId) return jobResponse;
            const managerUserIds = isManager
              ? [...jobResponse.managerUserIds, userId]
              : jobResponse.managerUserIds.filter((id) => id !== userId);
            return { ...jobResponse, managerUserIds };
          });
        },
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      queryClient.setQueryData(jobsQueryKey, context.previous);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
